import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useBackgroundTrack } from '../context/MusicContext';
import InAppBrowserWarning from '../components/InAppBrowserWarning';
import { detectInAppBrowser } from '../utils/inAppBrowser';

// Mirrors the password policy configured in Firebase Authentication settings.
// Update this constant whenever the Firebase policy changes.
const PASSWORD_POLICY = {
  customStrengthOptions: {
    minPasswordLength: 12,
    maxPasswordLength: 4096,
    containsUppercaseLetter: true,
    containsLowercaseLetter: true,
    containsNumericCharacter: true,
  },
};

export default function LoginPage() {
  const {
    user, profile, loading,
    signInWithGoogle,
    // signInWithMicrosoft, // uncomment when adding Microsoft sign-in
    // signInWithFacebook,  // uncomment when adding Facebook sign-in
    signUpWithEmail, signInWithEmail, sendPasswordReset,
  } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  useBackgroundTrack('home');

  const [showSignIn, setShowSignIn] = useState(false);
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (user && profile) navigate('/lobby', { replace: true });
    else if (user && !profile) navigate('/setup', { replace: true });
  }, [user, profile, loading, navigate]);

  function switchMode(newMode) {
    setPassword('');
    setConfirmPassword('');
    setError('');
    setResetSent(false);
    setSubmitAttempted(false);
    setMode(newMode);
  }

  function getErrorMessage(code) {
    switch (code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return t('auth.errors.invalidCredential');
      case 'auth/email-already-in-use':
        return t('auth.errors.emailInUse');
      case 'auth/weak-password':
        return t('auth.errors.weakPassword');
      case 'auth/invalid-email':
        return t('auth.errors.invalidEmail');
      case 'auth/too-many-requests':
        return t('auth.errors.tooManyRequests');
      case 'auth/account-exists-with-different-credential':
        return t('auth.errors.differentCredential');
      case 'auth/popup-blocked':
        return t('auth.errors.popupBlocked');
      default:
        return t('auth.errors.generic');
    }
  }

  async function handleOAuth(fn) {
    setError('');
    try {
      await fn();
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        setError(getErrorMessage(err.code));
      }
    }
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    setError('');
    if (mode === 'signup' && password !== confirmPassword) {
      setError(t('auth.errors.passwordMismatch'));
      return;
    }
    if (mode === 'signup') {
      const checks = checkPasswordRequirements(password, PASSWORD_POLICY);
      if (!Object.values(checks).every(Boolean)) {
        setSubmitAttempted(true);
        return;
      }
    }
    setSubmitting(true);
    try {
      if (mode === 'signup') {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err) {
      if (err.code === 'auth/password-does-not-meet-requirements') {
        setSubmitAttempted(true);
        setError(t('auth.errors.passwordDoesNotMeetRequirements'));
      } else {
        setError(getErrorMessage(err.code));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePasswordReset(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await sendPasswordReset(email);
      setResetSent(true);
    } catch (err) {
      setError(getErrorMessage(err.code));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return null;

  const iab = detectInAppBrowser();
  if (iab.detected) return <InAppBrowserWarning appName={iab.name} />;

  return (
    <div className="min-h-screen t-bg-page flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4" aria-hidden="true">🎵</div>
          <h1 className="text-3xl font-bold t-text mb-2">thatmusicgame</h1>
          <p className="t-text-muted">{t('auth.tagline')}</p>
        </div>

        <div className="t-bg-surface t-border border rounded-2xl p-6 mb-4 space-y-3 text-sm t-text-muted">
          <p>{t('auth.descParagraph1')}</p>
          <p>{t('auth.descParagraph2')}</p>
          <p>{t('auth.descParagraph3')}</p>
        </div>

        {!showSignIn ? (
          <button
            type="button"
            onClick={() => setShowSignIn(true)}
            className="w-full t-btn-primary font-medium rounded-xl px-4 py-3 transition-colors t-focus-ring"
          >
            {t('auth.signInToPlay')}
          </button>
        ) : (
          <div className="t-bg-surface t-border border rounded-2xl p-8 space-y-4">
            <h2 className="text-lg font-semibold t-text text-center">{t('auth.signInToPlay')}</h2>

            {error && (
              <p id="login-error" role="alert" className="t-text-error text-sm text-center t-bg-error t-border-error border rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <div className="space-y-3">
              <OAuthButton onClick={() => handleOAuth(signInWithGoogle)} icon={<GoogleIcon />}>
                {t('auth.continueWithGoogle')}
              </OAuthButton>
              {/* Microsoft and Facebook buttons — uncomment when providers are configured:
              <OAuthButton onClick={() => handleOAuth(signInWithMicrosoft)} icon={<MicrosoftIcon />}>
                Continue with Microsoft
              </OAuthButton>
              <OAuthButton onClick={() => handleOAuth(signInWithFacebook)} icon={<FacebookIcon />}>
                Continue with Facebook
              </OAuthButton>
              */}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full t-border border-t" />
              </div>
              <div className="relative flex justify-center text-xs t-text-disabled">
                <span className="t-bg-surface px-2">{t('auth.orContinueWithEmail')}</span>
              </div>
            </div>

            {mode === 'reset' ? (
              <form onSubmit={handlePasswordReset} className="space-y-3" noValidate>
                {resetSent ? (
                  <p className="t-text-success text-sm text-center">{t('auth.resetEmailSent')}</p>
                ) : (
                  <EmailInput value={email} onChange={e => setEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} />
                )}
                {!resetSent && (
                  <SubmitButton disabled={submitting}>
                    {submitting ? t('auth.sending') : t('auth.sendResetEmail')}
                  </SubmitButton>
                )}
                <button
                  type="button"
                  onClick={() => switchMode('signin')}
                  className="w-full text-sm t-text-muted hover:t-text transition-colors text-center t-focus-ring rounded"
                >
                  {t('auth.backToSignIn')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleEmailSubmit} className="space-y-3" noValidate>
                <EmailInput value={email} onChange={e => setEmail(e.target.value)} placeholder={t('auth.emailPlaceholder')} aria-describedby={error ? 'login-error' : undefined} />
                <PasswordInput
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  aria-describedby={[error ? 'login-error' : '', mode === 'signup' ? 'signup-pw-reqs' : ''].filter(Boolean).join(' ') || undefined}
                />
                {mode === 'signup' && (
                  <PasswordRequirements
                    id="signup-pw-reqs"
                    policy={PASSWORD_POLICY}
                    password={password}
                    submitAttempted={submitAttempted}
                  />
                )}
                {mode === 'signup' && (
                  <PasswordInput
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder={t('auth.confirmPasswordPlaceholder')}
                    autoComplete="new-password"
                  />
                )}
                <SubmitButton disabled={submitting}>
                  {submitting ? '…' : mode === 'signup' ? t('auth.createAccount') : t('auth.signIn')}
                </SubmitButton>
                <div className="flex items-center justify-between text-sm">
                  {mode === 'signin' ? (
                    <button
                      type="button"
                      onClick={() => switchMode('reset')}
                      className="t-text-muted hover:t-text transition-colors t-focus-ring rounded"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  ) : (
                    <span />
                  )}
                  <button
                    type="button"
                    onClick={() => switchMode(mode === 'signin' ? 'signup' : 'signin')}
                    className="t-text-accent hover:t-text transition-colors t-focus-ring rounded"
                  >
                    {mode === 'signin' ? t('auth.createAnAccount') : t('auth.signInInstead')}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-xs t-text-disabled">
          {t('auth.privacyNotice')}{' '}
          <Link to="/privacy" className="t-text-accent hover:t-text underline t-focus-ring rounded">
            {t('auth.privacyPolicy')}
          </Link>
          {' '}&amp;{' '}
          <Link to="/terms" className="t-text-accent hover:t-text underline t-focus-ring rounded">
            {t('auth.termsOfService')}
          </Link>
        </p>

        <div className="mt-4 text-xs t-text-disabled">
          <p className="font-medium t-text-muted mb-1">{t('auth.dataUsageHeading')}</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>{t('auth.dataUsageItem1')}</li>
            <li>{t('auth.dataUsageItem2')}</li>
            <li>{t('auth.dataUsageItem3')}</li>
            <li>{t('auth.dataUsageItem4')}</li>
            <li>{t('auth.dataUsageItem5')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function checkPasswordRequirements(pwd, policy) {
  const opts = policy.customStrengthOptions;
  const result = {};
  if (opts.minPasswordLength != null) result.minLength = pwd.length >= opts.minPasswordLength;
  if (opts.maxPasswordLength != null) result.maxLength = pwd.length <= opts.maxPasswordLength;
  if (opts.containsUppercaseLetter) result.uppercase = /[A-Z]/.test(pwd);
  if (opts.containsLowercaseLetter) result.lowercase = /[a-z]/.test(pwd);
  if (opts.containsNumericCharacter) result.numeric = /[0-9]/.test(pwd);
  return result;
}

function PasswordRequirements({ id, policy, password, submitAttempted }) {
  const { t } = useTranslation();
  const opts = policy.customStrengthOptions;
  const checks = checkPasswordRequirements(password, policy);

  const items = [
    opts.minPasswordLength != null && { key: 'minLength', label: t('auth.passwordReq.minLength', { count: opts.minPasswordLength }), met: checks.minLength },
    opts.maxPasswordLength != null && { key: 'maxLength', label: t('auth.passwordReq.maxLength', { count: opts.maxPasswordLength }), met: checks.maxLength },
    opts.containsUppercaseLetter && { key: 'uppercase', label: t('auth.passwordReq.uppercase'), met: checks.uppercase },
    opts.containsLowercaseLetter && { key: 'lowercase', label: t('auth.passwordReq.lowercase'), met: checks.lowercase },
    opts.containsNumericCharacter && { key: 'numeric', label: t('auth.passwordReq.numeric'), met: checks.numeric },
  ].filter(Boolean);

  if (items.length === 0) return null;

  return (
    <div id={id} className="text-xs space-y-1 pt-0.5" aria-live="polite">
      <p className="t-text-muted font-medium">{t('auth.passwordRequirementsLabel')}</p>
      <ul className="space-y-0.5">
        {items.map(item => {
          const isFailed = submitAttempted && !item.met;
          const isMet = item.met && password.length > 0;
          return (
            <li
              key={item.key}
              className={`flex items-center gap-1.5 ${isFailed ? 't-text-error' : isMet ? 't-text-success' : 't-text-muted'}`}
            >
              <span aria-hidden="true" className="w-3 shrink-0 text-center">{isFailed ? '✗' : isMet ? '✓' : '○'}</span>
              <span>{item.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function OAuthButton({ onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-3 bg-white text-gray-900 font-medium rounded-lg px-4 py-3 hover:bg-gray-100 transition-colors t-focus-ring"
    >
      {icon}
      {children}
    </button>
  );
}

function EmailInput({ value, onChange, placeholder, ...rest }) {
  return (
    <input
      type="email"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required
      autoComplete="email"
      className="w-full t-bg-input t-border-input border t-text rounded-lg px-4 py-3 text-sm t-placeholder t-focus-input"
      {...rest}
    />
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete, ...rest }) {
  return (
    <input
      type="password"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required
      autoComplete={autoComplete}
      className="w-full t-bg-input t-border-input border t-text rounded-lg px-4 py-3 text-sm t-placeholder t-focus-input"
      {...rest}
    />
  );
}

function SubmitButton({ children, disabled }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="w-full t-btn-primary disabled:opacity-50 font-medium rounded-lg px-4 py-3 transition-colors t-focus-ring"
    >
      {children}
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.259c-.806.54-1.837.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect x="0" y="0" width="8.5" height="8.5" fill="#F25022" />
      <rect x="9.5" y="0" width="8.5" height="8.5" fill="#7FBA00" />
      <rect x="0" y="9.5" width="8.5" height="8.5" fill="#00A4EF" />
      <rect x="9.5" y="9.5" width="8.5" height="8.5" fill="#FFB900" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      <path fill="#ffffff" d="M16.671 15.469l.532-3.47h-3.328v-2.25c0-.949.465-1.874 1.956-1.874h1.513V4.922s-1.374-.235-2.686-.235c-2.741 0-4.533 1.662-4.533 4.669v2.643H7.078v3.47h3.047v8.385a12.09 12.09 0 003.75 0v-8.385h2.796z" />
    </svg>
  );
}
